import { Building2, Images, MapPin } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Lightbox } from "../components/Lightbox";
import type { LightboxImage } from "../components/Lightbox";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/motion/Reveal";
import { Stagger, StaggerItem } from "../components/motion/Stagger";
import { apartments } from "../data/apartments";
import { siteInfo } from "../data/site";

type Chapter = { key: string; eyebrow: string; title: string; images: LightboxImage[] };

const chapters: Chapter[] = [
  {
    key: "building",
    eyebrow: "The Building",
    title: "A restored 1915 landmark",
    images: [
      { src: "/assets/site/clean-building-image.png", alt: "Exterior of The Deposit Seed House" },
      { src: "/assets/site/building-entry.jpg", alt: "Front entry of The Deposit Seed House" },
      { src: "/assets/site/exterior.png", alt: "Original block exterior and window openings" },
    ],
  },
  {
    key: "collection",
    eyebrow: "The Collection",
    title: "Art in the hallways",
    images: [{ src: "/assets/site/hall-art.jpg", alt: "Paintings and prints in the restored common hallway" }],
  },
  {
    key: "river",
    eyebrow: "The River",
    title: "On the East Branch of the Delaware",
    images: [
      { src: "/assets/local/anglers.jpg", alt: "Anglers fly fishing near Deposit, New York" },
      { src: "/assets/local/river.jpg", alt: "River scenery near Deposit, New York" },
    ],
  },
  {
    key: "residences",
    eyebrow: "The Residences",
    title: "Inside the apartments",
    images: apartments.slice(0, 6).map((apartment) => ({ src: apartment.featuredImage, alt: apartment.alt })),
  },
];

const allImages: LightboxImage[] = chapters.flatMap((chapter) => chapter.images);
const chapterBlocks = chapters.reduce<Array<Chapter & { start: number }>>((blocks, chapter) => {
  const start = blocks.reduce((total, block) => total + block.images.length, 0);
  blocks.push({ ...chapter, start });
  return blocks;
}, []);

export function Gallery() {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const openAt = (i: number) => {
    setIndex(i);
    setViewerOpen(true);
  };

  return (
    <>
      <SEO
        title="Gallery | The Deposit Seed House"
        description="The common hallways exhibit many interesting paintings and prints for your enjoyment."
        image={siteInfo.exteriorImage}
        canonicalPath="/gallery"
      />
      <section className="page-hero page-hero--gallery">
        <div>
          <p className="eyebrow">Gallery</p>
          <h1>See the spaces before you arrive.</h1>
          <p>Browse the building, furnished apartments, common areas, and the nearby river setting around The Deposit Seed House.</p>
          <div className="page-hero__stats" aria-label="Gallery summary">
            <span>
              <Building2 size={17} aria-hidden="true" />
              Restored stone building
            </span>
            <span>
              <Images size={17} aria-hidden="true" />
              {allImages.length} views
            </span>
            <span>
              <MapPin size={17} aria-hidden="true" />
              Deposit, New York
            </span>
          </div>
          <div className="page-hero__actions">
            <Link className="button button--primary" to="/apartments">
              View apartments
            </Link>
            <Link className="button button--light" to="/contact">
              Ask about a stay
            </Link>
          </div>
        </div>
      </section>
      {chapterBlocks.map((chapter) => (
        <section className="section gallery-chapter" key={chapter.key}>
          <Reveal className="section-heading section-heading--center gallery-chapter__heading">
            <p className="eyebrow">{chapter.eyebrow}</p>
            <h2>{chapter.title}</h2>
            <span className="rule-gold" aria-hidden="true" />
          </Reveal>
          <Stagger className="gallery-chapter__grid">
            {chapter.images.map((image, i) => (
              <StaggerItem as="figure" className="gallery-figure" key={`${image.src}-${i}`}>
                <button className="gallery-tile" type="button" onClick={() => openAt(chapter.start + i)} aria-label={`View ${image.alt ?? "photo"}`}>
                  <img src={image.src} alt={image.alt} loading="lazy" />
                </button>
                <figcaption className="serif-italic">{image.alt}</figcaption>
              </StaggerItem>
            ))}
          </Stagger>
        </section>
      ))}
      <Lightbox images={allImages} index={index} open={viewerOpen} title="The Deposit Seed House" onClose={() => setViewerOpen(false)} onIndexChange={setIndex} />
    </>
  );
}
